import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { Modal, Switch } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Button } from "@/components/bnp-ui";
import { PropertyEditFormData } from "@/types/admin";
import adminPropertyManagement from "@/services/admin/propertyManagement";
import userPropertyEditService from "@/services/userPropertyEdit";
import parseHttpError from "@/utils/parseHttpError";
import { toast } from "react-toastify";
import {
  getPropertyTypesByCategory,
  getPropertyTypeLabel,
  PROPERTY_CATEGORIES,
} from "./config/propertyCategories";
import CategorySpecificFields from "./CategorySpecificFields";
import MediaUpload from "@/components/bnp-ui/MediaUpload";
import {
  CommercialAmenity,
  GeneralAmenity,
  IndustrialAmenity,
  LandAmenity,
  ListingType,
  PropertyCategory,
  PropertyStage,
  PropertyType,
  ResidentialAmenity,
  SpecialPurposeAmenity,
} from "@/types/property-config";
import { Property } from "@/types/property";

interface UserPropertyEditFormProps {
  property: Property;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const formatNumber = (value: number | string | undefined): string => {
  if (value === undefined || value === null || value === "") return "";
  const num =
    typeof value === "string" ? parseFloat(value.replace(/,/g, "")) : value;
  if (isNaN(num)) return "";
  return num.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const parseFormattedNumber = (formatted: string): number => {
  const cleaned = formatted.replace(/,/g, "");
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

const MONEY_FIELDS = [
  "price",
  "total_package",
  "agency_fees",
  "legal_fees",
  "caution_fee",
] as const;

const haveSameImages = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const aSorted = [...a].sort();
  const bSorted = [...b].sort();
  for (let i = 0; i < aSorted.length; i++) {
    if (aSorted[i] !== bSorted[i]) return false;
  }
  return true;
};

const formatAmenityLabel = (str: string) => {
  return str
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const UserPropertyEditForm: React.FC<UserPropertyEditFormProps> = ({
  property,
  onSuccess,
  onCancel,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialImagesRef = useRef<string[]>(property.image_url || []);
  const [mediaUrls, setMediaUrls] = useState<string[]>(
    property.image_url || [],
  );

  const [reapprovalOpen, { open: openReapproval, close: closeReapproval }] =
    useDisclosure(false);
  const [pendingFormData, setPendingFormData] =
    useState<PropertyEditFormData | null>(null);
  const [category, setCategory] = useState<string>(
    property.property_category || "",
  );

  const [displayValues, setDisplayValues] = useState<Record<string, string>>(
    () => {
      const initial: Record<string, string> = {};
      MONEY_FIELDS.forEach((field) => {
        initial[field] = formatNumber((property as any)[field]);
      });
      return initial;
    },
  );

  const {
    register,
    handleSubmit,
    formState: { isDirty },
    setValue,
    watch,
  } = useForm<PropertyEditFormData>({
    defaultValues: {
      ...property,
      id: property.id || "",
      promote_to_weekly_deals: property.promote_to_weekly_deals ?? false,
      request_inspection: property.request_inspection ?? false,
      property_category: property.property_category || "",
      amenities: property.amenities || [],
    },
  });

  const selectedAmenities = watch("amenities") || [];
  const promoteWeekly = watch("promote_to_weekly_deals");
  const requestInspection = watch("request_inspection");

  const watchedPrice = watch("price");
  const watchedAgencyFees = watch("agency_fees");
  const watchedLegalFees = watch("legal_fees");
  const watchedCautionFee = watch("caution_fee");

  // Auto-recompute Total Package whenever the contributing fee fields change.
  useEffect(() => {
    const price = Number(watchedPrice) || 0;
    const agency = Number(watchedAgencyFees) || 0;
    const legal = Number(watchedLegalFees) || 0;
    const caution = Number(watchedCautionFee) || 0;
    const total = price + agency + legal + caution;
    setValue("total_package", total, { shouldDirty: true });
    setDisplayValues((prev) => ({
      ...prev,
      total_package: formatNumber(total),
    }));
  }, [
    watchedPrice,
    watchedAgencyFees,
    watchedLegalFees,
    watchedCautionFee,
    setValue,
  ]);

  const handleMoneyChange = (
    field: (typeof MONEY_FIELDS)[number],
    inputValue: string,
  ) => {
    const cleaned = inputValue.replace(/[^0-9.,]/g, "");
    const num = parseFormattedNumber(cleaned);
    setValue(field, num, { shouldDirty: true });
    setDisplayValues((prev) => ({ ...prev, [field]: cleaned }));
  };

  const handleMoneyBlur = (field: (typeof MONEY_FIELDS)[number]) => {
    const num = parseFormattedNumber(displayValues[field] || "0");
    setDisplayValues((prev) => ({ ...prev, [field]: formatNumber(num) }));
  };

  // Helper to resolve category specific amenities enum based on chosen category
  const getCategorySpecificAmenities = (): Record<string, string> => {
    const normCategory = (category || "").toUpperCase();
    switch (normCategory) {
      case "RESIDENTIAL":
        return ResidentialAmenity;
      case "COMMERCIAL":
        return CommercialAmenity;
      case "INDUSTRIAL":
        return IndustrialAmenity;
      case "LAND":
        return LandAmenity;
      case "SPECIAL_PURPOSE":
        return SpecialPurposeAmenity;
      default:
        return {};
    }
  };

  const handleAmenityToggle = (amenityValue: string) => {
    const current = [...selectedAmenities];
    const index = current.indexOf(amenityValue);

    if (index > -1) {
      current.splice(index, 1);
    } else {
      current.push(amenityValue);
    }

    setValue("amenities", current, { shouldDirty: true });
  };

  const submitToBackend = async (
    data: PropertyEditFormData,
    acknowledgeReapproval: boolean,
  ) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await userPropertyEditService.editOwnProperty({
        id: data.id,
        address: data.address,
        description: data.description,
        city: data.city,
        state: data.state,
        latitude: data.latitude,
        longitude: data.longitude,
        price: Number(data.price) || 0,
        totalPackage: Number(data.total_package) || 0,
        agencyFees: Number(data.agency_fees) || 0,
        legalFees: Number(data.legal_fees) || 0,
        cautionFee: Number(data.caution_fee) || 0,
        serviceFee: Number(data.service_fee) || 0,
        bedroomCount: data.bedroom_count,
        bathroomCount: data.bathroom_count,
        toilet: data.toilet,
        numberOfDays: data.number_of_days,
        numberOfPlots: data.number_of_plots,
        listingType: data.listing_type as ListingType,
        propertyType: data.property_type as PropertyType,
        propertyCategory: data.property_category as PropertyCategory,
        propertyStage: data.property_stage as PropertyStage,
        furnished: data.furnished,
        shared: data.shared,
        serviced: data.serviced,
        amenities: data.amenities,
        image_url: mediaUrls,
        promoteWeekly: data.promote_to_weekly_deals ?? false,
        requestInspection: data.request_inspection ?? false,
        additionalInfo: data.additional_info,
        saleDocumentDetails: data.sale_document_details,
        acknowledgeReapproval,
      });

      if (response.success) {
        toast.success(
          response.statusReset
            ? "Property updated. It is now back in pending approval for review."
            : "Property updated successfully",
        );
        onSuccess?.();
      } else if (response.errorCode === "REAPPROVAL_REQUIRED") {
        setPendingFormData(data);
        openReapproval();
      } else {
        setError(response.error || "Failed to update property");
      }
    } catch (err: any) {
      setError(parseHttpError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (data: PropertyEditFormData) => {
    setError(null);

    const validation = adminPropertyManagement.validatePropertyData(data);
    if (!validation.valid) {
      setError(validation.errors.join(", "));
      return;
    }

    const price = Number(data.price) || 0;
    const agency = Number(data.agency_fees) || 0;
    const legal = Number(data.legal_fees) || 0;
    const caution = Number(data.caution_fee) || 0;
    if (price > 0) {
      if (agency > price)
        return setError("Agency fee cannot exceed the property price.");
      if (legal > price)
        return setError("Legal fee cannot exceed the property price.");
      if (caution > price)
        return setError("Caution fee cannot exceed the property price.");
    }

    const wasApproved = property.status === "approved";
    const imagesChanged = !haveSameImages(initialImagesRef.current, mediaUrls);

    if (wasApproved && imagesChanged) {
      setPendingFormData(data);
      openReapproval();
      return;
    }

    await submitToBackend(data, false);
  };

  const handleConfirmReapproval = async () => {
    if (!pendingFormData) {
      closeReapproval();
      return;
    }
    closeReapproval();
    await submitToBackend(pendingFormData, true);
    setPendingFormData(null);
  };

  const handleCancelReapproval = () => {
    closeReapproval();
    setPendingFormData(null);
  };

  // Combine general + category specific amenity lists for rendering
  const categoryAmenities = getCategorySpecificAmenities();
  const allAvailableAmenities = Array.from(
    new Set([
      ...Object.values(GeneralAmenity),
      ...Object.values(categoryAmenities),
      ...selectedAmenities, // retain existing unknown backend strings
    ]),
  );

  return (
    <div className="bg-white rounded-lg p-2 sm:p-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-blue-primary font-medium text-sm mb-2">
              Property Title / Description
            </label>
            <input
              {...register("description")}
              className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
              placeholder="Enter property title or short description"
            />
          </div>

          <div>
            <label className="block text-blue-primary font-medium text-sm mb-2">
              Address
            </label>
            <input
              {...register("address")}
              className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
              placeholder="Enter address"
            />
          </div>

          <div>
            <label className="block text-blue-primary font-medium text-sm mb-2">
              Listing Type
            </label>
            <select
              {...register("listing_type")}
              className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
            >
              <option value="rent">Rent</option>
              <option value="sale">Sale</option>
              <option value="shortlet">Shortlet</option>
              <option value="land">Land</option>
            </select>
          </div>

          <div>
            <label className="block text-blue-primary font-medium text-sm mb-2">
              Category
            </label>
            <select
              {...register("property_category")}
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setValue("property_category", e.target.value, {
                  shouldDirty: true,
                });
              }}
              className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
            >
              <option value="">Select category</option>
              {Object.keys(PROPERTY_CATEGORIES).map((key) => (
                <option key={key} value={key}>
                  {
                    PROPERTY_CATEGORIES[key as keyof typeof PROPERTY_CATEGORIES]
                      .label
                  }
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-blue-primary font-medium text-sm mb-2">
              Property Type
            </label>
            <select
              {...register("property_type")}
              className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
            >
              <option value="">Select property type</option>
              {getPropertyTypesByCategory(category).map((type) => (
                <option key={type} value={type}>
                  {getPropertyTypeLabel(type)}
                </option>
              ))}
            </select>
          </div>

          {property.listing_type === "sale" && (
            <div>
              <label className="block text-blue-primary font-medium text-sm mb-2">
                Property Stage
              </label>
              <select
                {...register("property_stage")}
                className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
              >
                <option value="off-plan">Off-Plan</option>
                <option value="carcass">Carcass</option>
                <option value="semi-finished-carcass">
                  Semi-Finished Carcass
                </option>
                <option value="finished">Finished</option>
              </select>
            </div>
          )}
        </div>

        <CategorySpecificFields
          category={category}
          propertyType={property.property_type || ""}
          register={register}
          errors={{}}
          control={undefined}
          watch={watch}
        />

        {/* --- Amenities Section --- */}
        <div className="pt-2">
          <label className="block text-blue-primary font-medium text-sm mb-2">
            Amenities & Features
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Select features available on this property.
          </p>
          <div className="flex flex-wrap gap-2">
            {allAvailableAmenities.map((amenityVal) => {
              const isSelected = selectedAmenities.includes(amenityVal);
              return (
                <button
                  type="button"
                  key={amenityVal}
                  onClick={() => handleAmenityToggle(amenityVal)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-150 ease-in-out cursor-pointer flex items-center gap-1.5 ${
                    isSelected
                      ? "bg-bnp-cyan/10 border-bnp-cyan text-bnp-cyan shadow-sm"
                      : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <span>{formatAmenityLabel(amenityVal)}</span>
                  {isSelected && (
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2.5"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <div>
            <label className="block text-blue-primary font-medium text-sm mb-2">
              Price
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={displayValues.price}
              onChange={(e) => handleMoneyChange("price", e.target.value)}
              onBlur={() => handleMoneyBlur("price")}
              className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
              placeholder="Enter price"
            />
          </div>

          <div>
            <label className="block text-blue-primary font-medium text-sm mb-2">
              Total Package
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={displayValues.total_package}
              readOnly
              disabled
              className="w-full px-4 py-3 text-gray-600 bg-gray-100 shadow-sm border border-soft-200 rounded-lg focus:outline-none cursor-not-allowed"
              placeholder="Auto-calculated"
            />
            <p className="text-xs text-gray-500 mt-1">
              Auto-calculated as Price + Agency Fees + Legal Fees + Caution Fee.
            </p>
          </div>

          <div>
            <label className="block text-blue-primary font-medium text-sm mb-2">
              City
            </label>
            <input
              {...register("city")}
              className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
              placeholder="Enter city"
            />
          </div>

          <div>
            <label className="block text-blue-primary font-medium text-sm mb-2">
              State
            </label>
            <input
              {...register("state")}
              className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
              placeholder="Enter state"
            />
          </div>

          <div>
            <label className="block text-blue-primary font-medium text-sm mb-2">
              Bedroom Count
            </label>
            <input
              type="number"
              step="1"
              {...register("bedroom_count", { valueAsNumber: true })}
              className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
              placeholder="Number of bedrooms"
              min="0"
            />
          </div>

          <div>
            <label className="flex items-center space-x-2 mt-7">
              <input
                type="checkbox"
                {...register("furnished")}
                className="w-4 h-4 text-bnp-cyan border-gray-300 rounded focus:ring-bnp-cyan"
              />
              <span className="text-blue-primary font-medium text-sm">
                Furnished
              </span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-blue-primary font-medium text-sm mb-2">
              Agency Fees
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={displayValues.agency_fees}
              onChange={(e) => handleMoneyChange("agency_fees", e.target.value)}
              onBlur={() => handleMoneyBlur("agency_fees")}
              className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
              placeholder="Agency fees"
            />
          </div>

          <div>
            <label className="block text-blue-primary font-medium text-sm mb-2">
              Legal Fees
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={displayValues.legal_fees}
              onChange={(e) => handleMoneyChange("legal_fees", e.target.value)}
              onBlur={() => handleMoneyBlur("legal_fees")}
              className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
              placeholder="Legal fees"
            />
          </div>

          <div>
            <label className="block text-blue-primary font-medium text-sm mb-2">
              Caution Fee
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={displayValues.caution_fee}
              onChange={(e) => handleMoneyChange("caution_fee", e.target.value)}
              onBlur={() => handleMoneyBlur("caution_fee")}
              className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors"
              placeholder="Caution fee"
            />
          </div>
        </div>

        <div>
          <label className="block text-blue-primary font-medium text-sm mb-2">
            Property Media
          </label>

          {property.status === "approved" && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">
              Heads up: this listing is currently live. Adding or removing media
              will move it back to <strong>pending approval</strong> until our
              team re-reviews it.
            </p>
          )}

          <MediaUpload uploadedUrls={mediaUrls} onChange={setMediaUrls} />
        </div>

        <Switch
          checked={promoteWeekly}
          label={
            <div className="flex flex-col gap-1 mt-[-4px]">
              <p className="text-base font-medium text-main-900">
                Promote to weekly deals
              </p>
              <p className="text-sm text-grey-7">
                Listings pushed to weekly deals connote that customers will not
                be paying agency fee on the transactions.
              </p>
            </div>
          }
          onChange={() => {
            const next = !promoteWeekly;
            setValue("promote_to_weekly_deals", next, { shouldDirty: true });
            if (next) {
              setValue("agency_fees", 0, { shouldDirty: true });
            }
          }}
        />

        <Switch
          checked={requestInspection}
          className="cursor-pointer mt-5"
          label={
            <div className="flex flex-col gap-1 mt-[-4px]">
              <p className="text-base font-medium text-main-900">
                Request Live Inspection
              </p>
              <p className="text-sm text-grey-7">
                This will enable this property to be available for live
                inspection requests to be carried out on behalf of potential
                tenants.
              </p>
            </div>
          }
          onChange={() =>
            setValue("request_inspection", !requestInspection, {
              shouldDirty: true,
            })
          }
        />

        <div>
          <label className="block text-blue-primary font-medium text-sm mb-2">
            Additional Information
          </label>
          <textarea
            {...register("additional_info")}
            className="w-full px-4 py-3 text-gray-600 bg-white shadow-sm border border-soft-200 rounded-lg focus:outline-none focus:border-bnp-cyan transition-colors min-h-[160px] resize-none"
            placeholder="Anything else our team should know"
          />
        </div>

        <div className="flex justify-end space-x-4 pt-4">
          {onCancel && (
            <Button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            disabled={
              isSubmitting ||
              (!isDirty && haveSameImages(initialImagesRef.current, mediaUrls))
            }
            className={`px-6 py-2 bg-bnp-cyan text-white rounded-lg transition-colors ${
              isSubmitting ||
              (!isDirty && haveSameImages(initialImagesRef.current, mediaUrls))
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-opacity-90"
            }`}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>

      <Modal
        opened={reapprovalOpen}
        onClose={handleCancelReapproval}
        centered
        withCloseButton={false}
        radius="lg"
        size="md"
        title={
          <h3 className="text-blue-primary font-semibold text-lg">
            Re-approval required
          </h3>
        }
      >
        <p className="text-grey-7 text-sm mb-6">
          This property is currently <strong>live and approved</strong>. Because
          you added or removed an image, saving will move it back to{" "}
          <strong>pending approval</strong> until our team re-reviews it.
        </p>
        <p className="text-grey-7 text-sm mb-6">Do you want to continue?</p>
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            onClick={handleCancelReapproval}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirmReapproval}
            disabled={isSubmitting}
            className={`px-4 py-2 bg-bnp-cyan text-white rounded-lg transition-colors ${
              isSubmitting
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-opacity-90"
            }`}
          >
            {isSubmitting ? "Saving..." : "Yes, save anyway"}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default UserPropertyEditForm;
